/**
 * Legal Document Modal
 * Displays Terms of Service or Privacy Policy in a modal overlay
 *
 * Legal documents are stored as versioned markdown files in src/legal/
 * Filename version matches LEGAL_VERSIONS in server/config/constants.ts
 *
 * To update legal documents:
 * 1. For minor edits: Edit the existing markdown file directly
 * 2. For material changes that require re-acceptance:
 *    a. Create new version file (e.g., terms-of-service-v2.0.md)
 *    b. Update import below to point to new version
 *    c. Bump LEGAL_VERSIONS in server/config/constants.ts (e.g., '1.0' → '2.0')
 * 3. Keep old versions for audit trail (maps user acceptance to actual document content)
 */

import { X } from 'lucide-react';
import { type ReactElement, useEffect } from 'react';
import privacyContent from '../../legal/privacy-policy-v1.3.md?raw';
// Import versioned legal documents - update imports when legal docs change
import termsContent from '../../legal/terms-of-service-v2.2.md?raw';

type LegalDocType = 'terms' | 'privacy';

interface LegalModalProps {
  type: LegalDocType;
  onClose: () => void;
}

// Legal document content - imported from versioned markdown files
const LEGAL_CONTENT: Record<LegalDocType, { title: string; content: string }> = {
  terms: {
    title: 'Terms of Service',
    content: termsContent,
  },
  privacy: {
    title: 'Privacy Policy',
    content: privacyContent,
  },
};

export function LegalModal({ type, onClose }: LegalModalProps): ReactElement {
  const { title, content } = LEGAL_CONTENT[type];

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Simple markdown rendering
  const renderContent = (): ReactElement[] => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return (
          <h2 key={i} className="text-lg font-semibold text-theme-text mt-5 mb-2">
            {line.slice(3)}
          </h2>
        );
      }
      if (line.trim() === '---') {
        return <hr key={i} className="border-theme-border my-3" />;
      }
      if (line.startsWith('- ')) {
        return (
          <li key={i} className="text-theme-text text-sm ml-4">
            {line.slice(2)}
          </li>
        );
      }
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.+?)\*\*/g);
        return (
          <p key={i} className="text-theme-text text-sm mb-1">
            {parts.map((part, j) =>
              j % 2 === 1 ? (
                <strong key={j} className="text-theme-text">
                  {part}
                </strong>
              ) : (
                part
              ),
            )}
          </p>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-1" />;
      return (
        <p key={i} className="text-theme-text text-sm mb-1">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-theme-surface rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-theme-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme-border">
          <h2 className="text-xl font-bold text-theme-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-theme-text-secondary hover:text-theme-text transition p-1"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">{renderContent()}</div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-theme-border">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
